import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import FeatureTitle from 'components/_typography/FeatureTitle/FeatureTitle';

import useSeenGuides from './useSeenGuides';
import { expandTourRect, isTourTargetVisible, placeTourTooltip } from './tourSteps';
import type { TourRect, TourScenario } from './types';

const PREPARE_WAIT_MS = 320;
const HOLE_PADDING = 8;
const SPOTLIGHT_TRANSITION = 'top 0.28s ease, left 0.28s ease, width 0.28s ease, height 0.28s ease';
type Run = {
  scenario: TourScenario;
  stepIndex: number;
  rect: TourRect;
  /** Scenarios that will run after the current one (merged progress / Dalej). */
  upcoming: TourScenario[];
  /** Steps already finished in earlier scenarios of this session. */
  completedStepCount: number;
};

type StartOptions = {
  force?: boolean;
};

type TourContextValue = {
  run: Run | null;
  start: (scenarios: TourScenario | TourScenario[], options?: StartOptions) => void;
  enqueue: (scenario: TourScenario) => void;
  abort: () => void;
};

const TourContext = createContext<TourContextValue | null>(null);

const useStyles = makeStyles()(theme => ({
  root: {
    position: 'fixed',
    inset: 0,
    zIndex: 2000,
    pointerEvents: 'none',
  },
  dim: {
    position: 'fixed',
    background: 'rgba(15, 18, 40, 0.45)',
    pointerEvents: 'auto',
  },
  dimAnimated: {
    transition: SPOTLIGHT_TRANSITION,
  },
  hole: {
    position: 'fixed',
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}`,
    pointerEvents: 'auto',
  },
  holeAnimated: {
    transition: SPOTLIGHT_TRANSITION,
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'auto',
    background: theme.palette.common.white,
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    borderRadius: 12,
    padding: theme.spacing(2.5),
    boxShadow: '0 12px 40px rgba(15, 18, 40, 0.28)',
    animation: '$tooltipIn 0.28s ease',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3.5),
    },
  },
  '@keyframes tooltipIn': {
    from: {
      opacity: 0,
      transform: 'translateY(8px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  title: {
    minWidth: 0,
    flex: 1,
    paddingRight: theme.spacing(0.5),
  },
  close: {
    margin: theme.spacing(-1, -1, 0, 0),
    color: theme.palette.text.secondary,
  },
  description: {
    fontFamily: theme.typography.fontFamily,
    ...theme.typography.body2,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2.5),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(3),
    },
  },
  progress: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 700,
    letterSpacing: '0.04em',
    color: theme.palette.primary.main,
    marginRight: 'auto',
  },
}));

const wait = (ms: number) =>
  new Promise<void>(resolve => {
    window.setTimeout(resolve, ms);
  });

const nextFrame = () =>
  new Promise<void>(resolve => {
    window.requestAnimationFrame(() => resolve());
  });

const viewport = () => ({ width: window.innerWidth, height: window.innerHeight });

const measureTarget = (targetId: string): TourRect | null => {
  const element = document.getElementById(targetId);

  if (!element || !isTourTargetVisible(element)) {
    return null;
  }

  const raw = element.getBoundingClientRect();
  const view = viewport();
  const intersects = raw.bottom > 0 && raw.top < view.height && raw.right > 0 && raw.left < view.width;

  // Only skip when there is no intersection — a partial peek is enough to spotlight.
  if (!intersects) {
    return null;
  }

  const hole = expandTourRect(raw, HOLE_PADDING, view);

  if (hole.width < 8 || hole.height < 8) {
    return null;
  }

  return hole;
};

const upcomingStepCount = (upcoming: readonly TourScenario[]) =>
  upcoming.reduce((count, scenario) => count + scenario.steps.length, 0);

const scrollbarWidth = () => window.innerWidth - document.documentElement.clientWidth;

const SCROLLBAR_LOCK_CLASS = 'tour-scrollbar-hidden';

/** Hide the bar itself. Padding keeps the gutter so the page does not jump. */
const ensureScrollbarHiddenStyle = () => {
  if (document.getElementById(SCROLLBAR_LOCK_CLASS)) {
    return;
  }

  const style = document.createElement('style');
  style.id = SCROLLBAR_LOCK_CLASS;
  style.textContent = `
    html.${SCROLLBAR_LOCK_CLASS},
    html.${SCROLLBAR_LOCK_CLASS} body {
      scrollbar-width: none;
    }
    html.${SCROLLBAR_LOCK_CLASS}::-webkit-scrollbar,
    html.${SCROLLBAR_LOCK_CLASS} body::-webkit-scrollbar {
      width: 0;
      height: 0;
      display: none;
    }
  `;
  document.head.appendChild(style);
};

/** Lock page scroll without the layout jump from a disappearing scrollbar. */
const lockBodyScroll = (): (() => void) => {
  const { body } = document;
  const root = document.documentElement;
  const previousOverflow = body.style.overflow;
  const previousPaddingRight = body.style.paddingRight;
  const gap = scrollbarWidth();

  ensureScrollbarHiddenStyle();
  root.classList.add(SCROLLBAR_LOCK_CLASS);
  body.style.overflow = 'hidden';

  if (gap > 0) {
    body.style.paddingRight = `${gap}px`;
  }

  return () => {
    root.classList.remove(SCROLLBAR_LOCK_CLASS);
    body.style.overflow = previousOverflow;
    body.style.paddingRight = previousPaddingRight;
  };
};

/** Re-apply a scroll offset without kicking off another smooth animation. */
const withInstantScroll = (fn: () => void) => {
  const root = document.documentElement;
  const { body } = document;
  const previousRoot = root.style.scrollBehavior;
  const previousBody = body.style.scrollBehavior;

  root.style.scrollBehavior = 'auto';
  body.style.scrollBehavior = 'auto';
  fn();
  root.style.scrollBehavior = previousRoot;
  body.style.scrollBehavior = previousBody;
};

const scrollDeltaFor = (rect: DOMRect, viewHeight: number) => {
  const margin = 80;
  const fits = rect.height + margin * 2 <= viewHeight;
  const targetTop = fits ? (viewHeight - rect.height) / 2 : margin;

  return rect.top - targetTop;
};

/** Resolves once smooth scrolling has settled, or after `timeout` ms. */
const waitForScrollEnd = (scroller: Element, timeout = 900) =>
  new Promise<void>(resolve => {
    const started = performance.now();
    let last = scroller.scrollTop;
    let stableFrames = 0;

    const tick = () => {
      const current = scroller.scrollTop;

      if (Math.abs(current - last) < 0.5) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
        last = current;
      }

      if (stableFrames >= 4 || performance.now() - started > timeout) {
        resolve();
        return;
      }

      window.requestAnimationFrame(tick);
    };

    window.requestAnimationFrame(tick);
  });

/**
 * Smooth-scroll the target into view. The scrollbar stays hidden and padding-right
 * stays put. The spotlight is measured only after the scroll finishes.
 */
const scrollTourTargetIntoView = async (element: HTMLElement) => {
  const { body } = document;
  const wasLocked = body.style.overflow === 'hidden';
  const scroller = document.scrollingElement ?? document.documentElement;

  if (wasLocked) {
    ensureScrollbarHiddenStyle();
    document.documentElement.classList.add(SCROLLBAR_LOCK_CLASS);
    body.style.overflow = '';
    await nextFrame();
  }

  const delta = scrollDeltaFor(element.getBoundingClientRect(), window.innerHeight);

  if (Math.abs(delta) > 2) {
    scroller.scrollTo({ top: scroller.scrollTop + delta, behavior: 'smooth' });
    await waitForScrollEnd(scroller);
  }

  const kept = scroller.scrollTop;

  if (wasLocked) {
    body.style.overflow = 'hidden';
    withInstantScroll(() => {
      scroller.scrollTop = kept;
    });
    await nextFrame();
  }
};

type OverlayProps = {
  run: Run;
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
};

const TourOverlay: React.FC<OverlayProps> = ({ run, onPrev, onNext, onSkip }) => {
  const { classes, cx } = useStyles();
  const [spotlightReady, setSpotlightReady] = useState(false);
  const step = run.scenario.steps[run.stepIndex];
  // Cross-scenario back is not supported — hide Wstecz on the first step of each scenario.
  const canGoPrev = run.stepIndex > 0;
  const isLastInScenario = run.stepIndex === run.scenario.steps.length - 1;
  const isLastOverall = isLastInScenario && run.upcoming.length === 0;
  const totalSteps = run.completedStepCount + run.scenario.steps.length + upcomingStepCount(run.upcoming);
  const currentStepNumber = run.completedStepCount + run.stepIndex + 1;
  const { rect } = run;
  const placement = placeTourTooltip(rect, viewport());

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setSpotlightReady(true));

    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const blockEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('keydown', blockEscape, true);

    return () => window.removeEventListener('keydown', blockEscape, true);
  }, []);

  const dimClassName = cx(classes.dim, spotlightReady && classes.dimAnimated);
  const holeClassName = cx(classes.hole, spotlightReady && classes.holeAnimated);

  return (
    <div className={classes.root}>
      <div className={dimClassName} style={{ top: 0, left: 0, width: '100%', height: rect.top }} />
      <div className={dimClassName} style={{ top: rect.top, left: 0, width: rect.left, height: rect.height }} />
      <div
        className={dimClassName}
        style={{ top: rect.top, left: rect.right, width: `calc(100% - ${rect.right}px)`, height: rect.height }}
      />
      <div className={dimClassName} style={{ top: rect.bottom, left: 0, width: '100%', height: `calc(100% - ${rect.bottom}px)` }} />
      <div className={holeClassName} style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} />
      <div
        aria-modal='true'
        className={classes.tooltip}
        key={`${run.scenario.id}-${run.stepIndex}`}
        role='dialog'
        style={{ top: placement.top, left: placement.left, width: placement.width }}
      >
        <div className={classes.header}>
          <FeatureTitle className={classes.title} component='h2'>
            {step.title}
          </FeatureTitle>
          <IconButton aria-label='Zamknij samouczek' className={classes.close} onClick={onSkip} size='small'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </div>
        <Typography className={classes.description}>{step.description}</Typography>
        <div className={classes.actions}>
          <div className={classes.progress}>{`${currentStepNumber} z ${totalSteps}`}</div>
          {canGoPrev && (
            <Button color='primary' onClick={onPrev} size='small' variant='text'>
              Wstecz
            </Button>
          )}
          <Button color='primary' disableFocusRipple onClick={onNext} size='small' variant='contained'>
            {isLastOverall ? 'Zakończ' : 'Dalej'}
          </Button>
        </div>
      </div>
    </div>
  );
};

type ProviderProps = {
  children: React.ReactNode;
};

const TourProvider: React.FC<ProviderProps> = ({ children }) => {
  const { markSeen } = useSeenGuides();
  const [run, setRun] = useState<Run | null>(null);
  const runRef = useRef<Run | null>(null);
  const generation = useRef(0);
  const markSeenRef = useRef(markSeen);
  runRef.current = run;
  markSeenRef.current = markSeen;

  const finish = useCallback((guideId: string) => {
    generation.current += 1;
    setRun(null);
    void markSeenRef.current(guideId);
  }, []);

  const abort = useCallback(() => {
    generation.current += 1;
    setRun(null);
  }, []);

  const land = useCallback(
    async (
      scenario: TourScenario,
      startIndex: number,
      direction: 1 | -1,
      finishOnExhaust = true,
      upcoming: TourScenario[] = [],
      completedStepCount = 0,
    ) => {
      const token = generation.current + 1;
      generation.current = token;
      let index = startIndex;

      while (index >= 0 && index < scenario.steps.length) {
        const step = scenario.steps[index];
        step.prepare?.();

        if (step.prepare) {
          await wait(PREPARE_WAIT_MS);
        }

        await nextFrame();

        if (generation.current !== token) {
          return;
        }

        const element = document.getElementById(step.targetId);

        if (element && isTourTargetVisible(element)) {
          await scrollTourTargetIntoView(element);
        }

        if (generation.current !== token) {
          return;
        }

        let rect = measureTarget(step.targetId);

        // Element exists but still missed (e.g. long task note below the fold) — one hard retry.
        if (!rect && element && isTourTargetVisible(element)) {
          await scrollTourTargetIntoView(element);
          await wait(50);

          if (generation.current !== token) {
            return;
          }

          rect = measureTarget(step.targetId);
        }

        if (rect) {
          setRun({ scenario, stepIndex: index, rect, upcoming, completedStepCount });
          return;
        }

        index += direction;
      }

      if (generation.current !== token) {
        return;
      }

      if (finishOnExhaust) {
        const [next, ...rest] = upcoming;

        if (next) {
          void markSeenRef.current(scenario.id);
          void land(next, 0, 1, true, rest, completedStepCount + scenario.steps.length);
          return;
        }

        finish(scenario.id);
      }
    },
    [finish],
  );

  const start = useCallback(
    (scenarios: TourScenario | TourScenario[], options?: StartOptions) => {
      const list = (Array.isArray(scenarios) ? scenarios : [scenarios]).filter(Boolean);

      if (!list.length) {
        return;
      }

      if (runRef.current && !options?.force) {
        return;
      }

      const [first, ...rest] = list;
      void land(first, 0, 1, true, rest, 0);
    },
    [land],
  );

  const enqueue = useCallback((scenario: TourScenario) => {
    setRun(previous => {
      if (!previous) {
        return previous;
      }

      if (previous.scenario.id === scenario.id || previous.upcoming.some(item => item.id === scenario.id)) {
        return previous;
      }

      return { ...previous, upcoming: [...previous.upcoming, scenario] };
    });
  }, []);

  const goNext = useCallback(() => {
    const current = runRef.current;

    if (!current) {
      return;
    }

    if (current.stepIndex >= current.scenario.steps.length - 1) {
      const [next, ...rest] = current.upcoming;

      if (next) {
        void markSeenRef.current(current.scenario.id);
        void land(next, 0, 1, true, rest, current.completedStepCount + current.scenario.steps.length);
        return;
      }

      finish(current.scenario.id);
      return;
    }

    void land(
      current.scenario,
      current.stepIndex + 1,
      1,
      true,
      current.upcoming,
      current.completedStepCount,
    );
  }, [finish, land]);

  const goPrev = useCallback(() => {
    const current = runRef.current;

    if (!current || current.stepIndex === 0) {
      return;
    }

    // Missing previous targets must not finish the tour (that would open the next scenario).
    void land(current.scenario, current.stepIndex - 1, -1, false, current.upcoming, current.completedStepCount);
  }, [land]);

  const skip = useCallback(() => {
    const current = runRef.current;

    if (!current) {
      return;
    }

    finish(current.scenario.id);
  }, [finish]);

  const isRunning = Boolean(run);
  const activeStepKey = run ? `${run.scenario.id}:${run.stepIndex}` : null;

  const syncSpotlight = useCallback(() => {
    const current = runRef.current;

    if (!current) {
      return;
    }

    const rect = measureTarget(current.scenario.steps[current.stepIndex].targetId);

    if (!rect) {
      return;
    }

    setRun(previous => {
      if (!previous || previous.scenario.id !== current.scenario.id || previous.stepIndex !== current.stepIndex) {
        return previous;
      }

      const same =
        Math.abs(previous.rect.top - rect.top) < 1 &&
        Math.abs(previous.rect.left - rect.left) < 1 &&
        Math.abs(previous.rect.width - rect.width) < 1 &&
        Math.abs(previous.rect.height - rect.height) < 1;

      if (same) {
        return previous;
      }

      return { ...previous, rect };
    });
  }, []);

  useEffect(() => {
    if (!isRunning) {
      return undefined;
    }

    const unlock = lockBodyScroll();

    window.addEventListener('resize', syncSpotlight);

    return () => {
      unlock();
      window.removeEventListener('resize', syncSpotlight);
    };
  }, [isRunning, syncSpotlight]);

  // Layout keeps moving after the first measure (image load, the exercise
  // column's 300ms transition). Catch up so the hole matches the target.
  useEffect(() => {
    if (!activeStepKey) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(syncSpotlight);
    const timer = window.setTimeout(syncSpotlight, 360);
    const targetId = runRef.current?.scenario.steps[runRef.current.stepIndex]?.targetId;
    const element = targetId ? document.getElementById(targetId) : null;
    const observer = new ResizeObserver(syncSpotlight);

    if (element) {
      observer.observe(element);
    }

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [activeStepKey, syncSpotlight]);
  const value = useMemo(() => ({ run, start, enqueue, abort }), [run, start, enqueue, abort]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {run && <TourOverlay onNext={goNext} onPrev={goPrev} onSkip={skip} run={run} />}
    </TourContext.Provider>
  );
};

export const useTour = (): TourContextValue => {
  const value = useContext(TourContext);

  if (!value) {
    throw new Error('useTour must be used within TourProvider');
  }

  return value;
};

export default TourProvider;
